import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Drawer, Modal, message } from 'antd';
import {
  BulbOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  getResumePreviewHtml,
  downloadSearchResume,
  tailorSearchResume,
} from '../../services/api';
import { triggerBrowserDownload } from '../../utils/download';
import { setPendingResumeBind } from '../../utils/pendingResumeBind';
import { getApiErrorMessage } from '../../utils/errorUtils';
import PreviewPane from './PreviewPane';
import {
  RebuildExplainer,
  RebuildExplainerTitle,
  TemplateEvidence,
} from '../../pages/resumeTailor/resumeCoaching';
import {
  hasSeenRebuildExplainer,
  markRebuildExplainerSeen,
} from '../../utils/resumeTailorStorage';
import css from './ResumePreviewLink.module.scss';

export interface ResumePreviewLinkProps {
  /** The search whose bound resume to preview / download. */
  searchId: string;
  /** The resume VERSION bound to the search, when one exists — rendered read-only
   *  via GET /versions/{id}/preview. Null/absent for a plain base upload (no
   *  version row); that case previews the uploaded file itself instead. */
  resumeVersionId?: string | null;
  /** The search's role, shown in the drawer header. */
  role: string;
  /** True when the bound resume is this search's auto-tailored version — drives
   *  the header eyebrow (tailored vs base). Purely cosmetic; the download and
   *  preview always follow the bound resume either way. */
  tailored?: boolean;
}

/**
 * A small, universal "Resume" link on every /searches card. It opens a
 * read-only preview of the resume actually bound to the search — with a
 * Download — regardless of whether that resume was ever tailored.
 *
 * Two preview sources, one for each kind of bound resume:
 *  - Versioned (tailored, or a base opened in the editor): the rendered version
 *    HTML, via the same PreviewPane the editor uses (read-only here).
 *  - Base upload (no version row): the actual uploaded file — the exact blob
 *    Download serves. PDFs render inline; a .docx can't be shown in-browser, so
 *    it falls back to a download-to-view state.
 *
 * ADDITIVE to — never a replacement for — the emerald "Tailored for this role"
 * badge. The ids come from the live search object and the preview refetches on
 * every open, so an edit/revert that rebinds the resume is always reflected.
 */
const ResumePreviewLink: React.FC<ResumePreviewLinkProps> = ({
  searchId,
  resumeVersionId,
  role,
  tailored = false,
}) => {
  const versioned = !!resumeVersionId;
  // Only a rebuilt document has a layout to justify.
  const showWhy = tailored && versioned;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tailoring, setTailoring] = useState(false);
  // Versioned path: rendered HTML.
  const [html, setHtml] = useState('');
  // Base path: an object URL for the uploaded PDF, or 'unsupported' for a .docx
  // that browsers can't preview inline.
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobUnsupported, setBlobUnsupported] = useState(false);
  // First-run "why your resume looks different" — shares one flag with the
  // tailor editor, so whichever surface a user hits first is the one that
  // explains, once.
  const [explainerOpen, setExplainerOpen] = useState(false);
  // On-demand re-read of the same argument, at full detail.
  const [whyOpen, setWhyOpen] = useState(false);

  // Auto-tailoring means this drawer, not the editor, is often where someone
  // first meets a resume we rebuilt — and unlike the editor they never asked
  // for it. Explain before they can wonder. Guarded on `tailored` so a plain
  // base upload never fires it: there's nothing to justify when we haven't
  // touched the document.
  const handleOpen = () => {
    setOpen(true);
    if (showWhy && !hasSeenRebuildExplainer()) {
      setExplainerOpen(true);
    }
  };

  const handleExplainerDismiss = () => {
    setExplainerOpen(false);
    markRebuildExplainerSeen();
  };

  // Versioned preview. Fetch on open, and AGAIN if the bound version changes
  // underneath while the drawer stays open: the card's props are live, so an
  // edit/revert/auto-tailor rebind pushes a new resumeVersionId in and the
  // previewed document must re-sync (never "preview A, download B"). The
  // `cancelled` guard drops a slow earlier fetch so it can't overwrite a newer
  // version's document.
  useEffect(() => {
    if (!open || !versioned) return;
    let cancelled = false;
    setLoading(true);
    setHtml(''); // never flash a stale document before the fresh fetch lands
    getResumePreviewHtml(resumeVersionId as string)
      .then((h) => {
        if (!cancelled) setHtml(h);
      })
      .catch(() => {
        if (cancelled) return;
        message.error("Couldn't load this resume. Try again.");
        setOpen(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, versioned, resumeVersionId]);

  // Base-upload preview. No version to render — fetch the bound file (the same
  // blob Download serves) and show the actual document: a PDF inline, a .docx
  // as a download-to-view fallback. The object URL is revoked on cleanup so the
  // blob is released when the drawer closes or the resume rebinds.
  useEffect(() => {
    if (!open || versioned) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    setLoading(true);
    setBlobUrl(null);
    setBlobUnsupported(false);
    downloadSearchResume(searchId)
      .then(({ blob, filename }) => {
        if (cancelled) return;
        // The blob's own MIME is authoritative: the browser sets it from the
        // response Content-Type, which survives even when a proxy strips the
        // Content-Disposition (in which case downloadSearchResume defaults the
        // filename to 'resume.pdf' — so the extension can't be trusted alone).
        // Fall back to the extension only when the blob carries no type.
        const isPdf =
          blob.type === 'application/pdf' ||
          (blob.type === '' && /\.pdf$/i.test(filename));
        if (isPdf) {
          createdUrl = URL.createObjectURL(blob);
          setBlobUrl(createdUrl);
        } else {
          setBlobUnsupported(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        message.error("Couldn't load this resume. Try again.");
        setOpen(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      // Drop the revoked URL from state too. The component stays mounted while
      // the drawer is closed (it's the trigger), so without this a reopen would
      // paint one frame of an <iframe> pointing at the already-revoked blob URL
      // (a blank flash) before this effect reruns and refetches.
      setBlobUrl(null);
      setBlobUnsupported(false);
    };
  }, [open, versioned, searchId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await downloadSearchResume(searchId);
      triggerBrowserDownload(blob, filename);
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  // Tailor an untailored resume for this search. Mirrors the tailored chip's
  // Edit round-trip: create the tailor draft, stash the bind target so the
  // editor's approve binds it to this search, then hand off to the editor
  // (which polls in the role suggestion cards). On failure, surface a message
  // and stay put — never navigate into an editor with nothing forked.
  const handleTailor = async () => {
    setTailoring(true);
    try {
      const version = await tailorSearchResume(searchId);
      setPendingResumeBind({ versionId: version.id, searchId });
      navigate(`/resume/tailor/${version.id}`);
    } catch (error) {
      message.error(getApiErrorMessage(error));
      setTailoring(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={css.link}
        onClick={handleOpen}
        aria-label={`Preview resume for ${role}`}
      >
        <FileTextOutlined aria-hidden />
        <span>Resume</span>
      </button>
      {/* Version indicator, mirroring the tailored badge's "v{n}" pill: an
          untailored bound resume is the user's original upload. Tailored cards
          carry the emerald "Tailored · v{n}" badge instead, so suppress it there. */}
      {!tailored && <span className={css.originalPill}>Original</span>}
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width="min(720px, 94vw)"
        destroyOnClose
        title={
          <span className={css.header}>
            <span
              className={`${css.eyebrow} ${
                tailored ? css.eyebrowTailored : css.eyebrowBase
              }`}
            >
              {tailored ? 'Tailored for this role' : 'Base resume'}
            </span>
            <span className={css.role}>{role}</span>
          </span>
        }
        footer={
          <div className={css.footer}>
            {!tailored && (
              <Button
                icon={<ThunderboltOutlined />}
                loading={tailoring}
                onClick={handleTailor}
                className={css.tailorBtn}
              >
                Tailor
              </Button>
            )}
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownload}
              className={css.downloadBtn}
            >
              Download
            </Button>
          </div>
        }
      >
        {/* The answer stays one click away after the first-run modal retires —
            and only where we actually rebuilt something. */}
        {showWhy && (
          <div className={css.whyRow}>
            <button
              type="button"
              className={css.whyLayout}
              onClick={() => setWhyOpen(true)}
            >
              <BulbOutlined aria-hidden="true" /> Why this layout?
            </button>
          </div>
        )}
        {/* PreviewPane's .pane is position:absolute; inset:0 — it needs a
            positioned box with a real height or it collapses to nothing. */}
        <div
          className={`${css.previewBox} ${
            showWhy ? css.previewBoxWithWhy : ''
          }`}
        >
          {versioned ? (
            <PreviewPane
              html={html}
              loading={loading}
              interactive={false}
              onSectionClick={() => {}}
            />
          ) : loading ? (
            <div
              className={css.loadingState}
              role="status"
              aria-label="Loading resume preview"
            />
          ) : blobUrl ? (
            <iframe
              className={css.pdfFrame}
              src={blobUrl}
              title="Resume preview"
            />
          ) : blobUnsupported ? (
            <div className={css.fallback}>
              <FileTextOutlined aria-hidden className={css.fallbackIcon} />
              <p className={css.fallbackText}>
                Word documents can't be previewed here. Use Download to open the
                original file.
              </p>
            </div>
          ) : null}
        </div>

        {/* Both modals live INSIDE the Drawer so antd's zIndexContext (5.11+)
            lifts them above it — a sibling would render underneath. */}
        <Modal
          open={explainerOpen}
          onCancel={handleExplainerDismiss}
          footer={null}
          title={<RebuildExplainerTitle />}
          width="min(600px, 94vw)"
          // One-shot per user — don't leave a hidden copy in the tree.
          destroyOnClose
        >
          <RebuildExplainer
            variant="preview"
            onDismiss={handleExplainerDismiss}
          />
        </Modal>

        <Modal
          open={whyOpen}
          onCancel={() => setWhyOpen(false)}
          footer={null}
          title="Why this layout?"
          width="min(560px, 94vw)"
        >
          <TemplateEvidence />
        </Modal>
      </Drawer>
    </>
  );
};

export default ResumePreviewLink;
